import jsPDF from "jspdf";

// Date formatting
export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Currency symbols for display
export const getCurrencySymbol = (currency) => {
  switch (currency) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "INR":
    default:
      return "₹";
  }
};

// Currency symbols for PDF (PDF-compatible)
export const getCurrencySymbolForPDF = (currency) => {
  switch (currency) {
    case "USD":
      return "$";
    case "EUR":
      return "EUR";
    case "INR":
    default:
      return "Rs.";
  }
};

// Format amount with currency symbol
export const formatAmount = (amount, currency = "INR") => {
  const symbol = getCurrencySymbol(currency);
  const numValue = parseFloat(amount || 0);
  const formattedValue = numValue.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  return `${symbol} ${formattedValue}`;
};

// Format amount for PDF
export const formatAmountForPDF = (amount, currency = "INR") => {
  const symbol = getCurrencySymbolForPDF(currency);
  const numValue = parseFloat(amount || 0);
  const formattedValue = numValue.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${symbol} ${formattedValue}`;
};

// Format address
export const formatAddress = (address) => {
  if (!address) return "";
  const parts = [];
  if (address.building) parts.push(address.building);
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.district) parts.push(address.district);
  if (address.state) parts.push(address.state);
  if (address.pincode) parts.push(address.pincode);
  if (address.country) parts.push(address.country);
  return parts.join(", ");
};

// Generate PDF
export const generatePDF = (invoice, lawyerDetails, clientDetails) => {
  console.log("=== Generating PDF ===");
  console.log("Invoice Number from database:", invoice.invoiceNumber);
  console.log("Invoice Total Amount:", invoice.totalAmount);
  console.log("Invoice Currency:", invoice.currency);

  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 15;
  let yPosition = 25;

  // Case name at top left (if available) - Bold, larger font
  if (invoice.resourceType === "case" && invoice.resourceName) {
    pdf.setFontSize(14);
    pdf.setFont("times", "bold");
    const caseName = invoice.resourceName;
    const splitCaseName = pdf.splitTextToSize(caseName, 100);
    pdf.text(splitCaseName, margin, yPosition);
    yPosition += splitCaseName.length * 7;
  } else {
    yPosition += 5;
  }

  // "Invoice" title at top right - Large, bold
  pdf.setFontSize(32);
  pdf.setFont("times", "bold");
  pdf.text("Invoice", pageWidth - margin, 25, { align: "right" });

  yPosition += 10;

  // Lawyer Details on left side
  const detailsStartY = yPosition;
  pdf.setFontSize(11);
  pdf.setFont("times", "bold");
  if (lawyerDetails?.name) {
    pdf.text(lawyerDetails.name, margin, yPosition);
    yPosition += 6;
  }

  pdf.setFont("times", "normal");
  if (lawyerDetails?.phoneNumber) {
    pdf.text(`Phone: ${lawyerDetails.phoneNumber}`, margin, yPosition);
    yPosition += 6;
  }
  if (lawyerDetails?.email) {
    pdf.text(`Email: ${lawyerDetails.email}`, margin, yPosition);
    yPosition += 6;
  }

  // Invoice Number and Date on right side
  let rightY = detailsStartY;
  pdf.setFont("times", "normal");
  pdf.setFontSize(11);
  pdf.text(`Invoice #: ${invoice.invoiceNumber}`, pageWidth - margin, rightY, { align: "right" });
  rightY += 6;

  const invoiceDate = formatDate(invoice.raisedOn || new Date());
  pdf.text(`Date: ${invoiceDate}`, pageWidth - margin, rightY, { align: "right" });

  yPosition += 15;

  // Bill To Section - Dark header
  pdf.setFillColor(51, 51, 51);
  pdf.rect(margin, yPosition, pageWidth - 2 * margin, 10, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("times", "bold");
  pdf.setFontSize(12);
  pdf.text("Bill To", margin + 3, yPosition + 7);
  pdf.setTextColor(0, 0, 0);
  yPosition += 15;

  // Client Details
  pdf.setFont("times", "normal");
  pdf.setFontSize(11);
  if (clientDetails) {
    const clientName = clientDetails.salutation
      ? `${clientDetails.salutation} ${clientDetails.name}`
      : clientDetails.name;
    pdf.text(clientName, margin, yPosition);
    yPosition += 6;

    if (clientDetails.email) {
      pdf.text(`Email: ${clientDetails.email}`, margin, yPosition);
      yPosition += 6;
    }
  } else if (invoice.contactName) {
    pdf.text(invoice.contactName, margin, yPosition);
    yPosition += 6;
  }

  yPosition += 10;

  // Particulars Table Header
  const amountColumnX = pageWidth - margin - 10;
  pdf.setFillColor(51, 51, 51);
  pdf.rect(margin, yPosition, pageWidth - 2 * margin, 10, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("times", "bold");
  pdf.setFontSize(12);
  pdf.text("Particulars", margin + 3, yPosition + 7);
  pdf.text("Amount", amountColumnX, yPosition + 7, { align: "right" });
  pdf.setTextColor(0, 0, 0);
  yPosition += 15;

  // Particulars Items
  pdf.setFont("times", "normal");
  pdf.setFontSize(11);

  if (invoice.particulars && invoice.particulars.length > 0) {
    invoice.particulars.forEach((particular) => {
      pdf.text(particular.description, margin + 3, yPosition);
      const amountText = formatAmountForPDF(particular.amount, invoice.currency);
      pdf.text(amountText, amountColumnX, yPosition, { align: "right" });
      yPosition += 7;
    });
  }

  // Selected Expenses
  if (invoice.selectedExpenses && invoice.selectedExpenses.length > 0 && invoice.selectedCase) {
    const expenses = invoice.selectedCase.expenses || [];
    invoice.selectedExpenses.forEach((expenseId) => {
      const expense = expenses.find((e) => e.id === expenseId);
      if (expense) {
        const description = expense.description || expense.type;
        pdf.text(description, margin + 3, yPosition);
        const amountText = formatAmountForPDF(expense.amount, invoice.currency);
        pdf.text(amountText, amountColumnX, yPosition, { align: "right" });
        yPosition += 7;
      }
    });
  }

  // Draw line before total
  yPosition += 2;
  pdf.setDrawColor(200, 200, 200);
  pdf.line(pageWidth - margin - 60, yPosition, amountColumnX, yPosition);
  yPosition += 8;

  // Total
  pdf.setFont("times", "bold");
  pdf.setFontSize(12);
  const totalLabelX = pageWidth - margin - 60;
  pdf.text("Total:", totalLabelX, yPosition);
  const totalAmountText = formatAmountForPDF(invoice.totalAmount, invoice.currency);
  pdf.text(totalAmountText, amountColumnX, yPosition, { align: "right" });

  return pdf;
};
